import { Badge } from '@repo/ui/badge'
import { Skeleton } from '@repo/ui/skeleton'
import { useQuery } from '@tanstack/react-query'
import {
	createFileRoute,
	Link,
	stripSearchParams
} from '@tanstack/react-router'
import { Paperclip } from 'lucide-react'
import { z } from 'zod'
import { useTranslation } from '@/lib/i18n'
import { useWorkingCall } from '../../../-hooks/use-working-call'
import { formatRomeMoment } from '../../../-lib/rome-time'
import { rankingReportsQueryOptions } from './-query/query-options'

const searchSchema = z.object({
	state: z.enum(['open', 'closed']).default('open')
})

export const Route = createFileRoute(
	'/(private)/bando/$residenceId/graduatoria/segnalazioni/'
)({
	component: ReportsPage,
	validateSearch: searchSchema,
	search: {
		middlewares: [stripSearchParams(searchSchema.parse({}))]
	}
})

/**
 * The reports the students sent about the ranking (FFD 2.8): the open ones
 * first, each with what it says, who sent it and where its application stands.
 * A report is read, compared with the application and closed; nothing here
 * changes a position.
 */
function ReportsPage() {
	const { t, i18n } = useTranslation()
	const { residenceId } = Route.useParams()
	const { state } = Route.useSearch()
	const { callId } = useWorkingCall()
	const query = useQuery(
		rankingReportsQueryOptions(residenceId, state, callId)
	)

	return (
		<div className='mx-auto flex w-full max-w-4xl flex-col gap-6 p-4 md:p-6'>
			<header className='flex flex-col gap-3'>
				<Link
					className='text-muted-foreground text-sm hover:underline'
					params={{ residenceId }}
					to='/bando/$residenceId/graduatoria'
				>
					{t('admin-ranking-reports:back')}
				</Link>
				<h1 className='font-semibold text-2xl'>
					{t('admin-ranking-reports:page_title')}
				</h1>
				<nav className='flex gap-2'>
					{(['open', 'closed'] as const).map((item) => (
						<Link
							aria-current={state === item ? 'page' : undefined}
							className={`rounded-full border border-border px-3 py-1.5 text-sm ${
								state === item
									? 'bg-primary text-primary-foreground'
									: 'hover:bg-muted'
							}`}
							key={item}
							params={{ residenceId }}
							search={{ state: item }}
							to='/bando/$residenceId/graduatoria/segnalazioni'
						>
							{t(`admin-ranking-reports:filters.${item}`)}
						</Link>
					))}
				</nav>
			</header>
			{query.isLoading && (
				<div className='flex flex-col gap-3'>
					<Skeleton className='h-24 w-full' />
					<Skeleton className='h-24 w-full' />
				</div>
			)}
			{query.data?.length === 0 && (
				<p className='text-muted-foreground text-sm'>
					{t(`admin-ranking-reports:empty_${state}`)}
				</p>
			)}
			<ul className='flex flex-col gap-3'>
				{query.data?.map((report) => (
					<li
						className='relative flex flex-col gap-2 rounded-xl border border-border bg-card p-4 transition-colors focus-within:ring-3 focus-within:ring-ring/50 hover:bg-muted/40'
						key={report.id}
					>
						<div className='flex flex-wrap items-center justify-between gap-2'>
							<Link
								className='font-medium outline-none after:absolute after:inset-0 after:rounded-xl'
								params={{ residenceId, reportId: report.id }}
								to='/bando/$residenceId/graduatoria/segnalazioni/$reportId'
							>
								{[
									report.student.lastName,
									report.student.firstName
								]
									.filter(Boolean)
									.join(' ')}
								{' · '}
								<span className='tabular-nums'>
									{t('admin-ranking-reports:row.protocol', {
										protocol: report.student.protocol
									})}
								</span>
							</Link>
							<Badge
								variant={
									report.state === 'open'
										? 'attention'
										: 'neutral'
								}
							>
								{t(
									`admin-ranking-reports:state.${report.state}`
								)}
							</Badge>
						</div>
						<p className='text-sm'>
							{t(`admin-ranking-reports:type.${report.type}`)}
						</p>
						<p className='flex flex-wrap items-center gap-x-3 gap-y-1 text-muted-foreground text-xs'>
							<span className='tabular-nums'>
								{t('admin-ranking-reports:row.sent', {
									when: formatRomeMoment(
										report.createdAt,
										i18n.language
									)
								})}
							</span>
							<span>
								{t('admin-ranking-reports:row.version', {
									number: report.versionNumber
								})}
							</span>
							{report.hasFile && (
								<span className='flex items-center gap-1'>
									<Paperclip aria-hidden className='size-3' />
									{t('admin-ranking-reports:row.with_file')}
								</span>
							)}
						</p>
					</li>
				))}
			</ul>
		</div>
	)
}
